import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { BalanceType, UserInfoType } from '../../../api/fetchers/userInfo/types';
import { Logger } from '../../../logger/Logger';
import { CoreSessionStorage } from '../../../storage/session/CoreSessionStorage';
import { getCurrencyInfo } from '../../../utils/formatters';
import { fetchUserInfoAction } from '../../apiActions/fetchUserInfoAction';

import {
  type CurrencyInfo,
  type DealerType,
  type TableCloseNotifyType,
  type TableType,
  type WinnersTotal,
  type WinnerType,
  type WinResultType,
  GameStatus,
} from './types';

declare global {
  interface Window {
    videoPoster?: string;
  }
}

const initialState: {
  balanceCheckCount: number;
  currencyInfo: CurrencyInfo | null;
  currentStatus: GameStatus | string;
  dealer: DealerType | null;
  gameAbandoned: boolean;
  gameChannel: string | null;
  gameId: string | null;
  gameName: string | null;
  gamePaused: boolean;
  gameSuspended: boolean;
  inactivityCounter: number;
  isLoadedInActiveRound: boolean;
  leftDrawerOpened: string | null; // TODO: do same with right drawer
  netPosition: number;
  table: TableType | null;
  tableCloseNotify: TableCloseNotifyType | null;
  timer: number;
  timerStartTime: number;
  userInfo: UserInfoType | null;
  videoPoster: string;
  winResult: WinResultType | null;
  winners: WinnerType[];
  winnersTotal: WinnersTotal | null;
} = {
  balanceCheckCount: 4,
  currencyInfo: null,
  currentStatus: GameStatus.WaitForNextGame,
  dealer: null,
  gameAbandoned: false,
  gameChannel: null,
  gameId: null,
  gameName: null,
  gamePaused: false,
  gameSuspended: false,
  inactivityCounter: 0,
  isLoadedInActiveRound: true,
  leftDrawerOpened: null,
  netPosition: 0,
  table: null,
  tableCloseNotify: null,
  timer: 0,
  timerStartTime: 0,
  userInfo: null,
  videoPoster: window.videoPoster || '',
  winResult: null,
  winners: [],
  winnersTotal: null,
};

export const gameStateSlice = createSlice({
  name: 'gameState',
  initialState,
  reducers: {
    setWinners: (state, action: PayloadAction<WinnerType[]>) => {
      state.winners = action.payload;
    },
    setWinnersTotal: (state, action: PayloadAction<WinnersTotal | null>) => {
      state.winnersTotal = action.payload;
    },
    setWinResult: (state, action: PayloadAction<WinResultType | null>) => {
      state.winResult = action.payload;
    },
    setCurrentGameStatus: (state, action: PayloadAction<GameStatus | string>) => {
      state.currentStatus = action.payload;
      Logger.updateGameState(action.payload);
    },
    resetCurrentGameStatus: (state) => {
      state.currentStatus = GameStatus.WaitForNextGame;
    },
    setTimer: (state, action: PayloadAction<number>) => {
      state.timer = action.payload;
      state.timerStartTime = Date.now();
    },
    setGameId: (state, action: PayloadAction<string>) => {
      state.gameId = action.payload;
    },
    setGameChannel: (state, action: PayloadAction<string>) => {
      state.gameChannel = action.payload;
    },
    setGameName: (state, action: PayloadAction<string>) => {
      state.gameName = action.payload;
    },
    setUserInfo: (state, action: PayloadAction<UserInfoType>) => {
      state.userInfo = action.payload;
    },
    setBalance: (state, action: PayloadAction<number>) => {
      state.userInfo?.balance ? (state.userInfo.balance.amount = action.payload) : null;
    },
    updateBalance: (state, action: PayloadAction<Partial<BalanceType>>) => {
      state.userInfo?.balance ? (state.userInfo.balance = { ...state.userInfo.balance, ...action.payload }) : null;
    },
    setInactivityCounter: (state, action: PayloadAction<number>) => {
      state.inactivityCounter = action.payload;
    },
    setNetPosition: (state, action: PayloadAction<number>) => {
      state.netPosition = action.payload;
    },
    setOpenLeftDrawer: (state, action: PayloadAction<string | null>) => {
      state.leftDrawerOpened = action.payload;
    },
    setDealer: (state, action: PayloadAction<DealerType>) => {
      state.dealer = action.payload;
    },
    setTable: (state, action: PayloadAction<TableType>) => {
      state.table = action.payload;
    },
    setBalanceCheckCount: (state, action: PayloadAction<number>) => {
      state.balanceCheckCount = action.payload;
    },
    setGamePaused: (state, action: PayloadAction<boolean>) => {
      state.gamePaused = action.payload;
    },
    setGameAbandoned: (state, action: PayloadAction<boolean>) => {
      state.gameAbandoned = action.payload;
    },
    setGameSuspended: (state, action: PayloadAction<boolean>) => {
      state.gameSuspended = action.payload;
    },
    setIsLoadedInActiveRound: (state, action: PayloadAction<boolean>) => {
      state.isLoadedInActiveRound = action.payload;
    },
    setVideoPoster: (state, action: PayloadAction<string>) => {
      state.videoPoster = action.payload;
    },
    resetForNewGame: (state) => {
      state.winResult = null;
    },
    setTableCloseNotify: (state, action: PayloadAction<TableCloseNotifyType>) => {
      state.tableCloseNotify = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserInfoAction.fulfilled, (state, action) => {
      state.userInfo = action.payload;
      const {
        balance: { currency },
      } = action.payload;
      state.currencyInfo = getCurrencyInfo(currency, CoreSessionStorage.getLang());
    });
  },
});

export const gameStateActions = gameStateSlice.actions;
